// ManageFiltersModal.jsx
import React, { useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@/design-system/native";
import { Search as SearchIcon, Sliders, Edit, X } from "lucide-react";
import DataTable from "../../Tables/DataTable/DataTable";
import TableCard from "../../Tables/DataTable/TableCard";
import Pager from "../../Tables/DataTable/Pagination";
import styles from "./ManageFiltersModal.module.scss";


export default function ManageFiltersModal({
  open,
  toggle,
  filters,
  onApply,
  onEdit,
  onDelete,
}) {
  // ---------- Search ----------
  const [search, setSearch] = useState("");

  // ---------- Sorting + Paging ----------
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ---------- Derived ----------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return filters.filter((f) => {
      return (
        !q ||
        f.title.toLowerCase().includes(q) ||
        (f.bookmark ? "yes" : "no").includes(q) ||
        (f.icon ? f.icon.toLowerCase() : "").includes(q)
      );
    });
  }, [filters, search]);

  const columns = useMemo(
    () => [
      { id: "title", header: "Title", accessor: "title", width: 250 },
      {
        id: "bookmark",
        header: "Bookmark",
        accessor: (row) => (row.bookmark ? "Yes" : "No"),
        width: 150,
      },
      {
        id: "icon",
        header: "Bookmark Icon",
        accessor: (row) => row.icon || "-",
        width: 150,
      },
      {
        id: "actions",
        header: "",
        width: 400,
        cell: (row) => (
          <div className={styles.actions}>
            <Button
              size="md"
              color="light"
              className={styles.applyBtn}
              onClick={() => onApply(row)}
              title="Change filters"
            >
              <Sliders size={14} color="#10161b" /> Change filters
            </Button>
            <Button
              size="xs"
              color="light"
              className={styles.iconBtn}
              onClick={() => onEdit(row)}
            >
              <Edit size={16} color="#10161b" />
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => onDelete(row.id)}
              className={styles.iconBtn}
            >
              <X color="#10161b" size={18} />
            </Button>
          </div>
        ),
      },
    ],
    [onApply, onEdit, onDelete]
  );

  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const col = columns.find((c) => c.id === sortBy);
    if (!col) return filtered;

    const getVal = (r) =>
      typeof col.accessor === "function" ? col.accessor(r) : r[col.accessor];

    const arr = [...filtered].sort((a, b) => {
      const A = getVal(a);
      const B = getVal(b);
      return String(A).toLowerCase().localeCompare(String(B).toLowerCase());
    });
    return sortDir === "asc" ? arr : arr.reverse();
  }, [filtered, sortBy, sortDir, columns]);

  const total = sorted.length;
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  // ---------- Render ----------
  const rightSlot = (
    <div className={styles.searchWrap}>
      <Input
        type="search"
        placeholder="Search"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className={styles.searchInput}
      />
      <SearchIcon size={16} className={styles.searchIcon} />
    </div>
  );

  return (
    <Modal isOpen={open} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Manage Filters</ModalHeader>
      <ModalBody>
        <TableCard title="" rightSlot={rightSlot}>
          <div className={styles.tableWrap}>
            <DataTable
              columns={columns}
              rows={paged}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={(id, dir) => {
                setSortBy(id);
                setSortDir(dir);
              }}
            />
          </div>

          <Pager
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            pageSizes={[5, 10, 20, filters.length]}
          />
        </TableCard>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
